import BaseController from "confinus_ui/base_controller";
import { localeMessage } from "lib/i18n";
import { showNotification } from "lib/overlay/notification";

// Drives the buyer "enter details" checkout form. The form opts out of Turbo
// Drive (turbo: false) and is submitted here via fetch against the JSON
// endpoint, which responds with `{payment_session_id}` on success (200) or
// `{message}` JSON on error (422). On success we hand off to Stripe.js
// redirectToCheckout. Replaces the old Rails-UJS remote form + pay_now_with_stripe.js.erb.
//
// The form's own Decor validation runs first and stops propagation when the
// form is invalid, so submit() only fires for a validated form.
declare global {
  interface Window {
    Stripe?: (key: string) => {
      redirectToCheckout: (opts: {
        sessionId: string;
      }) => Promise<{ error?: { message: string } }>;
    };
  }
}

export default class CheckoutFormController extends BaseController {
  public static values = {
    stripeKey: String,
  };

  declare private readonly stripeKeyValue: string;

  public async submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const body = new FormData(form);

    let response: Response;
    try {
      response = await fetch(form.action, {
        method: "POST",
        body,
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
    } catch (e) {
      this.showError(localeMessage("generic_server_error"));
      return;
    }

    if (response.ok) {
      const { payment_session_id: sessionId } = await response.json();
      await this.redirectToStripe(sessionId);
    } else {
      let message: string | undefined;
      try {
        message = (await response.json()).message;
      } catch (e) {
        // fall through to generic message
      }
      this.showError(message ?? localeMessage("generic_server_error"));
    }
  }

  private async redirectToStripe(sessionId: string): Promise<void> {
    if (typeof window.Stripe !== "function") {
      this.showError(localeMessage("generic_server_error"));
      return;
    }
    const stripe = window.Stripe(this.stripeKeyValue);
    const result = await stripe.redirectToCheckout({ sessionId });
    if (result.error) {
      this.showError(result.error.message);
    }
  }

  private showError(message: string): void {
    showNotification({ variant: "error", body: message, timeout: 5000 });
  }
}
